import { Injectable, Injector, ComponentRef, TemplateRef } from '@angular/core';
import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal, PortalInjector } from '@angular/cdk/portal';

import { ToastRef } from './toast-ref';
import { ToastContainer } from './toast-container.component';
import { TOAST_CONFIG_DATA } from './toast.tokens';

export type ToastType = 'info' | 'success' | 'attention' | 'alert';

export interface ToastContent {
  type: ToastType;
  body?: string | TemplateRef<any>;
}

export interface ToastConfig {
  content?: ToastContent;
  position?: {
    top: number;
  };
  animation?: {
    fadeOut: number;
    fadeIn: number;
  };
}

const DEFAULT_CONFIG: ToastConfig = {
  position: {
    top: 20,
  },
  animation: {
    fadeOut: 2500,
    fadeIn: 150,
  },
  content: null,
};

@Injectable()
export class ToastService {
  private lastToast: ToastRef;

  constructor(
    private injector: Injector,
    private overlay: Overlay
  ) { }

  show(config: ToastConfig = {}) {
    // Override default configuration
    const dialogConfig = { ...DEFAULT_CONFIG, ...config };

    // Returns an OverlayRef which is a PortalHost
    const overlayRef = this.createOverlay(dialogConfig);

    // Instantiate remote control
    const toastRef = new ToastRef(overlayRef);

    this.lastToast = toastRef;

    const overlayComponent = this.attachToastContainer(overlayRef, dialogConfig, toastRef);

    toastRef.componentInstance = overlayComponent;

    return toastRef;
  }

  private createOverlay(config: ToastConfig) {
    const overlayConfig = this.getOverlayConfig(config);
    return this.overlay.create(overlayConfig);
  }

  private attachToastContainer(overlayRef: OverlayRef, config: ToastConfig, toastRef: ToastRef) {
    const injector = this.createInjector(config, toastRef);

    const containerPortal = new ComponentPortal(ToastContainer, null, injector);
    const containerRef: ComponentRef<ToastContainer> = overlayRef.attach(containerPortal);

    return containerRef.instance;
  }

  private createInjector(config: ToastConfig, toastRef: ToastRef): PortalInjector {
    const injectionTokens = new WeakMap();

    injectionTokens.set(ToastRef, toastRef);
    injectionTokens.set(TOAST_CONFIG_DATA, config);

    return new PortalInjector(this.injector, injectionTokens);
  }

  private getOverlayConfig(config: ToastConfig): OverlayConfig {
    const positionStrategy = this.overlay.position()
      .global()
      .centerHorizontally()
      .top(this.getPosition(config));

    const overlayConfig = new OverlayConfig({
      positionStrategy
    });

    return overlayConfig;
  }

  getPosition(config: ToastConfig) {
    const lastToastIsVisible = this.lastToast && this.lastToast.isVisible();
    const position = lastToastIsVisible
      ? this.lastToast.getPosition().bottom
      : config.position.top;

    return position + 'px';
  }

}
